import { useEffect, useRef, type RefObject } from 'react';
import { useDrawingStore } from '@/features/drawing/model';

interface UseResizeHandlerProps {
  containerRef: RefObject<HTMLDivElement | null>;
  canvasRef: RefObject<HTMLCanvasElement | null>;
  minZoomRef: RefObject<number>;
  transformRef: RefObject<{ zoom: number; pan: { x: number; y: number } }>;
  isMobile: boolean;
}

export function useResizeHandler({
  containerRef,
  canvasRef,
  minZoomRef,
  transformRef,
  isMobile,
}: UseResizeHandlerProps) {
  const { textBlocks, setCanvasTransform } = useDrawingStore();

  // Track last valid container dimensions to detect keyboard-triggered resizes
  const lastContainerDimensionsRef = useRef<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });

  useEffect(() => {
    const handleResize = () => {
      const canvas = canvasRef.current;
      const container = containerRef.current;
      if (!canvas || !container) return;

      // On mobile, ignore resize events when text block is active
      // This prevents canvas distortion when mobile keyboard opens/closes
      if (isMobile && textBlocks.activeBlockId !== null) {
        return;
      }

      // Recalculate fit when window resizes
      const containerRect = container.getBoundingClientRect();
      const padding = isMobile ? 20 : 40;
      const availableWidth = containerRect.width - padding * 2;
      const availableHeight = containerRect.height - padding * 2;

      // Validate container dimensions before recalculating
      if (
        containerRect.width < 100 ||
        containerRect.height < 100 ||
        availableWidth < 50 ||
        availableHeight < 50
      ) {
        return;
      }

      // On mobile, detect keyboard-triggered resize by checking if only height changed significantly
      if (isMobile && lastContainerDimensionsRef.current.width > 0) {
        const widthChange = Math.abs(
          containerRect.width - lastContainerDimensionsRef.current.width
        );
        const heightChange = Math.abs(
          containerRect.height - lastContainerDimensionsRef.current.height
        );

        // If width didn't change much but height changed significantly, this is likely keyboard
        if (widthChange < 5 && heightChange > 100) {
          return;
        }
      }

      // Store current dimensions for next comparison
      lastContainerDimensionsRef.current = {
        width: containerRect.width,
        height: containerRect.height,
      };

      const scaleX = availableWidth / canvas.width;
      const scaleY = availableHeight / canvas.height;
      const fitScale = Math.min(scaleX, scaleY, 3);

      // Validate fitScale is reasonable
      if (fitScale < 0.01 || fitScale > 10) {
        return;
      }

      // Update minimum zoom
      minZoomRef.current = Math.max(fitScale * 0.8, 0.1);

      // On mobile, don't auto-reset zoom on resize
      if (!isMobile) {
        const currentZoom = transformRef.current.zoom;
        if (currentZoom > fitScale * 2) {
          setCanvasTransform({
            zoom: fitScale,
            pan: { x: 0, y: 0 },
          });
        }
      }
    };

    // Debounce resize with longer delay on mobile
    let resizeTimer: NodeJS.Timeout;
    const debouncedResize = () => {
      clearTimeout(resizeTimer);
      const delay = isMobile ? 300 : 200;
      resizeTimer = setTimeout(handleResize, delay);
    };

    window.addEventListener('resize', debouncedResize);

    return () => {
      window.removeEventListener('resize', debouncedResize);
      clearTimeout(resizeTimer);
    };
  }, [
    setCanvasTransform,
    isMobile,
    textBlocks.activeBlockId,
    canvasRef,
    containerRef,
    minZoomRef,
    transformRef,
  ]);

  return { lastContainerDimensionsRef };
}
